import React, { useState, useEffect } from 'react';
import { Table, Input, Tag, Switch, Modal, Button, Select, Tooltip, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EyeOutlined, SearchOutlined, DeleteOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getUsersList, deleteUserDetails } from '../../redux/admin/actionCreator';

function UsersList() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [passwordModal, setPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [editModal, setEditModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);

  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const { getuserlist, loading } = useSelector((state) => state.AdminDashboard);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    dispatch(getUsersList(pagination.current, pagination.pageSize, debouncedSearch));
  }, [dispatch, pagination.current, pagination.pageSize, debouncedSearch]);

  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    plan: '',
    status: true,
  });

  const handleDeleteClick = (record) => {
    setSelectedUserForDelete(record);
    setDeleteModal(true);
  };

  const confirmDeleteUser = () => {
    if (!selectedUserForDelete) return;
    setDeleteLoading(true);
    dispatch(
      deleteUserDetails(selectedUserForDelete.user_id, (success, response) => {
        setDeleteLoading(false);
        setDeleteModal(false);
        if (success) {
          message.success(response?.message || 'User deleted successfully.');
          dispatch(getUsersList(pagination.current, pagination.pageSize, debouncedSearch));
        } else {
          message.error(response?.message || 'Failed to delete user.');
        }
        setSelectedUserForDelete(null);
      }),
    );
  };

  const dataSource =
    getuserlist?.data?.map((item) => ({
      key: item.user_id,
      user_id: item.user_id,
      name: item.name,
      email: item.email,
      is_active: item.is_active,
      business_name: item.business_name,
      mobile_number: item.mobile_number,
      address: item.address,
      city: item.city,
      state: item.state,
      pin_code: item.pin_code,

      subscription: item.subscription, // add this
      created_at: item.created_at,
    })) || [];

  // const filteredUsers = dataSource.filter(
  //   (user) =>
  //     user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     user?.email?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     user?.mobile_number?.includes(searchText),
  // );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 70,
      align: 'center',
      render: (v) => (
        <Tooltip title={v} color="black" overlayInnerStyle={{ color: '#fff' }}>
          <span className="font-medium text-[#111827] block truncate cursor-pointer" style={{ maxWidth: '220px' }}>
            {v || '-'}
          </span>
        </Tooltip>
      ),
    },

    {
      title: 'Email',
      dataIndex: 'email',
      width: 70,
      align: 'center',
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v} color="black" overlayInnerStyle={{ color: '#fff' }}>
          <span className="font-medium text-[#111827] block truncate cursor-pointer" style={{ maxWidth: '220px' }}>
            {v}
          </span>
        </Tooltip>
      ),
    },

    {
      title: 'Mobile',
      dataIndex: 'mobile_number',
      width: 70,
      align: 'center',
      render: (value) => <span className="font-medium text-[#111827]">{value || '-'}</span>,
    },

    {
      title: 'Business',
      dataIndex: 'business_name',
      width: 70,
      align: 'center',
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v} color="black" overlayInnerStyle={{ color: '#fff' }}>
          <span className="font-medium text-[#111827] block truncate cursor-pointer" style={{ maxWidth: '220px' }}>
            {v}
          </span>
        </Tooltip>
      ),
    },

    {
      title: 'City',
      dataIndex: 'city',
      width: 70,
      align: 'center',
      render: (value) => <span className="font-medium text-[#111827]">{value || '-'}</span>,
    },

    {
      title: 'State',
      dataIndex: 'state',
      width: 70,
      align: 'center',
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v} color="black" overlayInnerStyle={{ color: '#fff' }}>
          <span className="font-medium text-[#111827] block truncate cursor-pointer" style={{ maxWidth: '220px' }}>
            {v}
          </span>
        </Tooltip>
      ),
    },

    {
      title: 'Pin Code',
      dataIndex: 'pin_code',
      width: 70,
      align: 'center',
      render: (value) => <span className="font-medium text-[#111827]"> {value || '-'}</span>,
    },

    {
      title: 'Subscription',
      // dataIndex: 'subscription_status',
      width: 70,
      align: 'center',
      render: (_, record) => {
        if (!record?.subscription) {
          return '-';
        }

        return (
          <Tag color={record.subscription.status === 'active' ? 'green' : 'red'}>{record.subscription.status}</Tag>
        );
      },
    },

    {
      title: 'Plan',
      // dataIndex: 'subscription_plan',
      width: 70,
      align: 'center',
      render: (_, record) => <Tag color="blue">{record?.subscription?.plan_name || ''}</Tag>,
    },

    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 80,
      align: 'center',
      render: (isActive) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>,
    },

    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 70,
      align: 'center',
      render: (date) => (date ? new Date(date).toLocaleDateString('en-IN') : '-'),
    },

    {
      title: 'Details',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <Button
            type="text"
            icon={<EyeOutlined className="text-[#1677ff] text-[18px]" />}
            onClick={() => {
              navigate('/super-admin/user/view', {
                state: {
                  userId: record.user_id,
                },
              });
            }}
          />

          <Button
            type="text"
            danger
            icon={<DeleteOutlined className="text-red-500 text-[18px]" />}
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      ),
    },

    // {
    //   title: 'Password',
    //   width: 120,
    //   align: 'center',
    //   render: (_, record) => (
    //     <Button
    //       size="small"
    //       icon={<LockOutlined />}
    //       onClick={() => {
    //         setSelectedUser(record);
    //         setPasswordModal(true);
    //       }}
    //     >
    //       Reset
    //     </Button>
    //   ),
    // },

    // {
    //   title: '',
    //   width: 80,
    //   align: 'center',
    //   render: (_, record) => (
    //     <Dropdown
    //       trigger={['click']}
    //       menu={{
    //         items: [
    //           {
    //             key: 'edit',
    //             label: 'Edit User',
    //             onClick: () => {
    //               setSelectedUser(record);

    //               setEditForm({
    //                 name: record.name,
    //                 phone: record.mobile_number,
    //                 plan: record.subscription_plan || '',
    //                 status: record.subscription_active,
    //               });

    //               setEditModal(true);
    //             },
    //           },
    //           {
    //             key: 'history',
    //             label: 'Login History',
    //             onClick: () => {
    //               setSelectedUser(record);
    //               setHistoryModal(true);
    //             },
    //           },
    //           {
    //             key: 'delete',
    //             danger: true,
    //             label: 'Delete User',
    //           },
    //         ],
    //       }}
    //     >
    //       <MoreOutlined className="cursor-pointer text-[18px]" />
    //     </Dropdown>
    //   ),
    // },
  ];

  return (
    <>
      <div className="p-4 min-h-screen">
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-3">
            <div>
              <h2 className="mb-0 text-[20px] font-semibold">Users Management</h2>

              {/* <p className="mb-0 text-[12px] text-gray-500">Manage all platform users</p> */}
            </div>

            {/* <Button
            type="primary"
            className="h-[30px] text-[12px] font-semibold flex items-center"
            icon={<PlusOutlined />}
          >
            Add User
          </Button> */}
          </div>

          {/* Search */}
          {/* Search */}
          <div className="p-2 border-b">
            <Input
              placeholder="Search user..."
              suffix={
                searchText ? (
                  <button
                    type="button"
                    onClick={() => setSearchText('')}
                    className="flex items-center justify-center cursor-pointer text-[#9ca3af] hover:text-[#374151]"
                  >
                    <CloseCircleOutlined style={{ fontSize: 16 }} />
                  </button>
                ) : (
                  <SearchOutlined style={{ fontSize: 14, color: '#9ca3af' }} />
                )
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-80 h-[35px] rounded-lg border border-[#e5e7eb] bg-white text-[12px] outline-none shadow-sm focus:border-[#1677ff]"
              size="small"
            />
          </div>

          <div className="p-2">
            <Table
              rowKey="user_id"
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              size="small"
              scroll={{ x: 1000 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: getuserlist?.pagination?.total_records || 0,
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
              onChange={(pag) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize,
                });
              }}
              className="
    [&_.ant-table-thead>tr>th]:!text-[12px]
    [&_.ant-table-thead>tr>th]:!font-semibold
    [&_.ant-table-tbody>tr>td]:!text-[12px]
    [&_.ant-table-cell]:!px-2
    [&_.ant-table-cell]:!py-2
  "
            />
          </div>
        </div>
      </div>
      <Modal
        title={null}
        open={passwordModal}
        onCancel={() => setPasswordModal(false)}
        footer={null}
        width={480}
        centered
      >
        <div className="py-1">
          <h2 className="text-[22px] font-semibold text-[#111827] mb-5">Change Password</h2>

          {/* User Info */}
          {/* <div className="mb-5 p-3 rounded-xl border border-[#e5e7eb] bg-[#fafafa]">
            <p className="mb-0 text-[14px] font-semibold text-[#111827]">{selectedUser?.name}</p>

            <p className="mb-0 text-[12px] text-[#6b7280]">{selectedUser?.email}</p>
          </div> */}

          {/* New Password */}
          <div className="mb-4">
            <label className="block text-[12px] font-medium text-[#111827] mb-2">New Password</label>

            <Input.Password
              size="large"
              value={password}
              placeholder="Enter new password"
              onChange={(e) => setPassword(e.target.value)}
              className="h-[42px] rounded-xl"
            />

            <div className="mt-2 space-y-1">
              <p className="text-[11px] text-[#ef4444] mb-2">
                Please add all necessary characters to create safe password.
              </p>

              <div className="flex items-center gap-2 text-[11px] text-[#9ca3af]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#bdbdbd]" />
                <span>Minimum characters 12</span>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-[#ef4444]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444]" />
                <span>One uppercase character</span>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-[#9ca3af]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#bdbdbd]" />
                <span>One lowercase character</span>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-[#9ca3af]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#bdbdbd]" />
                <span>One special character</span>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-[#9ca3af]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#bdbdbd]" />
                <span>One number</span>
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-[12px] font-medium text-[#111827] mb-2">Confirm New Password</label>

            <Input.Password
              size="large"
              value={confirmPassword}
              placeholder="Enter confirm password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-[42px] rounded-xl"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2">
            <Button className="rounded-lg" onClick={() => setPasswordModal(false)}>
              Cancel
            </Button>

            <Button type="primary" className="rounded-lg px-6">
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      <Modal title="Edit User" open={editModal} onCancel={() => setEditModal(false)} footer={null} width={500}>
        <div className="space-y-4">
          <div>
            <label className="block text-[12px] font-medium mb-1">Full Name</label>

            <Input
              value={editForm.name}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  name: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Mobile Number</label>

            <Input
              value={editForm.phone}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  phone: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Subscription Plan</label>

            <Select
              value={editForm.plan}
              style={{ width: '100%' }}
              onChange={(value) =>
                setEditForm({
                  ...editForm,
                  plan: value,
                })
              }
              options={[
                { value: 'Basic', label: 'Basic' },
                { value: 'Premium', label: 'Premium' },
                { value: 'Pro', label: 'Pro' },
              ]}
            />
          </div>

          <div className="flex items-center justify-between bg-[#f8fafc] p-3 rounded-lg">
            <div>
              <p className="mb-0 font-medium">User Status</p>

              <p className="mb-0 text-[12px] text-gray-500">Enable or disable account</p>
            </div>

            <Switch
              checked={editForm.status}
              onChange={(checked) =>
                setEditForm({
                  ...editForm,
                  status: checked,
                })
              }
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setEditModal(false)}>Cancel</Button>

            <Button type="primary">Save Changes</Button>
          </div>
        </div>
      </Modal>

      <Modal
        title="Login History"
        open={historyModal}
        footer={null}
        onCancel={() => setHistoryModal(false)}
        width={500}
      >
        <div className="space-y-3">
          <div className="border rounded-lg p-3">
            <div className="font-medium">Chrome - Windows</div>

            <div className="text-gray-500 text-[12px]">11 Jun 2026, 10:30 AM</div>
          </div>

          <div className="border rounded-lg p-3">
            <div className="font-medium">Mobile App</div>

            <div className="text-gray-500 text-[12px]">10 Jun 2026, 08:12 PM</div>
          </div>

          <div className="border rounded-lg p-3">
            <div className="font-medium">Chrome - Mac</div>

            <div className="text-gray-500 text-[12px]">09 Jun 2026, 11:40 AM</div>
          </div>
        </div>
      </Modal>

      <Modal
        open={subscriptionModal}
        footer={null}
        width={500}
        centered
        onCancel={() => {
          setSubscriptionModal(false);
          setSelectedSubscription(null);
        }}
      >
        {selectedSubscription && (
          <div>
            <div className="border-b pb-4 mb-4">
              <h2 className="text-[22px] font-semibold mb-1">{selectedSubscription.plan_name}</h2>

              <Tag color={selectedSubscription.status === 'active' ? 'green' : 'red'}>
                {selectedSubscription.status}
              </Tag>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">Billing Cycle</div>
                <div className="font-semibold capitalize">{selectedSubscription.billing_cycle}</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">Amount</div>
                <div className="font-semibold">₹{selectedSubscription.amount}</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">Start Date</div>
                <div className="font-semibold">
                  {new Date(selectedSubscription.start_date).toLocaleDateString('en-IN')}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">End Date</div>
                <div className="font-semibold">
                  {new Date(selectedSubscription.end_date).toLocaleDateString('en-IN')}
                </div>
              </div>

              {/* <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">Payment Status</div>
                <Tag color={selectedSubscription.is_paid ? 'green' : 'orange'}>
                  {selectedSubscription.is_paid ? 'Paid' : 'Pending'}
                </Tag>
              </div> */}

              {/* <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-[12px]">Subscription ID</div>
                <div className="font-semibold">#{selectedSubscription.subscription_id}</div>
              </div> */}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={deleteModal}
        centered
        width={420}
        footer={null}
        closable={false}
        onCancel={() => {
          setDeleteModal(false);
          setSelectedUserForDelete(null);
        }}
      >
        <div className="py-2 text-center">
          <DeleteOutlined
            style={{
              fontSize: 42,
              color: '#ef4444',
              marginBottom: 12,
            }}
          />

          <h3 className="text-lg font-semibold mb-2">Delete User</h3>

          <p className="text-gray-500 mb-1">
            Are you sure you want to delete user{' '}
            <strong>{selectedUserForDelete?.name || selectedUserForDelete?.email}</strong>?
          </p>
          <p className="text-xs text-red-500 mb-6">
            This will set the user&apos;s account and linked sub-accounts to Inactive.
          </p>

          <div className="flex justify-center gap-3">
            <Button
              onClick={() => {
                setDeleteModal(false);
                setSelectedUserForDelete(null);
              }}
            >
              Cancel
            </Button>

            <Button danger type="primary" loading={deleteLoading} onClick={confirmDeleteUser}>
              Delete User
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default UsersList;
